// src/EmployeesSettings.js
import React, { useState, useEffect } from 'react';
import TitleBar from './components/ui/TitleBar';
import NotificationOverlayFrame from './components/NotificationOverlayFrame';
import useTimedUnsavedWarning from './useTimedUnsavedWarning';

import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
const API_BASE = getApiBase();

// ── Mobile helper ──────────────────────────────────────────────────────────
const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const compareVisibleNames = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, {
  sensitivity: 'base',
  numeric: true
});

const PREDEFINED_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#64748b'
];

const EmployeesSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [employees, setEmployees] = useState([]);
  const [savedEmployeeIds, setSavedEmployeeIds] = useState([]);
  const { unsavedWarningVisible, showUnsavedWarning, clearUnsavedWarning } = useTimedUnsavedWarning();
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  const [employeeForm, setEmployeeForm] = useState({
    name: '',
    email: '',
    phone: '',
    color: '#3b82f6',
    isActive: true
  });

  const sortedEmployees = [...employees].sort((a, b) => compareVisibleNames(a.name, b.name));

  // ── Responsive listener ────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile()); // force correct value after mount
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizeEmployeesPayload = (data) => {
    const list = Array.isArray(data) ? data : (Array.isArray(data?.employees) ? data.employees : []);
    return list.map(e => ({
      id: e.id,
      isNew: false,
      name: e.name || '',
      email: e.email || '',
      phone: e.phone || '',
      color: e.color || '#3b82f6',
      isActive: typeof e.isActive === 'number' ? e.isActive === 1 : (e.isActive !== false)
    }));
  };

  // ── Load employees ─────────────────────────────────────────────────────────
  const loadEmployees = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/employees`, {
        headers: { ...getTenantHeaders() }
      });
      if (res.ok) {
        const data = await res.json();
        const normalized = normalizeEmployeesPayload(data);
        setEmployees(normalized);
        setSavedEmployeeIds(normalized.map(employee => String(employee.id)));
        clearUnsavedWarning();
      } else {
        console.error('Failed to load employees settings. Status:', res.status);
      }
    } catch (error) {
      console.error('Failed to load employees settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (value) => {
    const formatted = formatPhone(value);
    setEmployeeForm({ ...employeeForm, phone: formatted });
  };

  // ── Save employees ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const retainedIds = new Set(
        employees
          .filter(employee => !employee.isNew)
          .map(employee => String(employee.id))
      );
      const deletedIds = savedEmployeeIds.filter(id => !retainedIds.has(id));

      for (const employee of employees) {
        const res = await fetch(
          employee.isNew
            ? `${API_BASE}/api/settings/employees`
            : `${API_BASE}/api/settings/employees/${employee.id}`,
          {
            method: employee.isNew ? 'POST' : 'PUT',
            headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
            body: JSON.stringify({
              name: String(employee.name || '').trim(),
              email: String(employee.email || '').trim(),
              phone: String(employee.phone || '').trim(),
              color: employee.color || '#3b82f6',
              ...(employee.isNew ? { role: 'worker' } : {}),
              isActive: employee.isActive ? 1 : 0
            })
          }
        );

        if (!res.ok) throw new Error(`Save failed (HTTP ${res.status})`);

        if (employee.isNew) {
          const data = await res.json();
          if (!data.id) throw new Error('Save failed: missing employee ID');

          setEmployees(prev => prev.map(item =>
            item.id === employee.id
              ? { ...item, id: data.id, isNew: false }
              : item
          ));
        }
      }

      for (const id of deletedIds) {
        const res = await fetch(`${API_BASE}/api/settings/employees/${id}`, {
          method: 'DELETE',
          headers: { ...getTenantHeaders() }
        });
        if (!res.ok) throw new Error(`Save failed (HTTP ${res.status})`);
      }

      clearUnsavedWarning();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'EMPLOYEES SAVED!' }
      }));
      await loadEmployees();
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save settings: ' + error.message);
      await loadEmployees();
    } finally {
      setSaving(false);
    }
  };

  const addEmployee = () => {
    const name = employeeForm.name.trim();
    const email = employeeForm.email.trim();
    const phone = (employeeForm.phone || '').trim();

    if (!name) return alert('Enter employee name');
    if (!email) return alert('Enter employee email');

    const newEmployee = {
      id: Date.now(),
      isNew: true,
      name,
      email,
      phone,
      color: employeeForm.color,
      isActive: !!employeeForm.isActive
    };

    setEmployees(prev => [...prev, newEmployee]);
    showUnsavedWarning();
    setEmployeeForm({ name: '', email: '', phone: '', color: '#3b82f6', isActive: true });
    setShowAddEmployee(false);
  };

  const startEditEmployee = (employee) => {
    setEditingEmployee(employee.id);
    setEmployeeForm({
      name: employee.name,
      email: employee.email,
      phone: employee.phone || '',
      color: employee.color || '#3b82f6',
      isActive: employee.isActive !== false
    });
  };

  const saveEditEmployee = () => {
    const name = employeeForm.name.trim();
    const email = employeeForm.email.trim();
    const phone = (employeeForm.phone || '').trim();

    if (!name) return alert('Enter employee name');
    if (!email) return alert('Enter employee email');

    setEmployees(prev => prev.map(e =>
      e.id === editingEmployee
        ? {
            ...e,
            name,
            email,
            phone,
            color: employeeForm.color,
            isActive: !!employeeForm.isActive
          }
        : e
    ));
    showUnsavedWarning();

    setEditingEmployee(null);
    setEmployeeForm({ name: '', email: '', phone: '', color: '#3b82f6', isActive: true });
  };

  const deleteEmployee = (id) => {
    if (!window.confirm('DELETE THIS EMPLOYEE?')) return;
    setEmployees(prev => prev.filter(e => e.id !== id));
    showUnsavedWarning();
  };

  const toggleActive = (id) => {
    setEmployees(prev => prev.map(e =>
      e.id === id ? { ...e, isActive: !e.isActive } : e
    ));
    showUnsavedWarning();
  };

  const styles = {
    page: {
      padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'),
      backgroundColor: '#E8E2DA',
      minHeight: '100vh'
    },
    contentWrapper: {
      maxWidth: "1200px", padding: isPhone ? '0' : "0 12px",
      margin: '0 auto'
    },
    panel: {
      backgroundColor: '#E7B888',
      border: isMobile ? '6px solid black' : '10px solid black',
      padding: isPhone ? '15px 5px' : (isMobile ? '15px' : '40px'),
      marginBottom: '18px',
    },
    label: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '13px',
      marginBottom: '6px',
      display: 'block'
    },
    input: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box',
      backgroundColor: 'white'
    },
    row1: { marginBottom: '12px' },
    row2: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: '12px',
      marginBottom: '12px'
    },
    addBtn: {
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px 20px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '14px',
      marginBottom: '12px'
    },
    card: {
      border: '4px solid black',
      padding: '14px',
      marginBottom: '12px',
      backgroundColor: 'white'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '10px',
      flexWrap: 'wrap'
    },
    cardInfo: { flex: 1, minWidth: '200px' },
    cardName: {
      fontWeight: 900,
      fontSize: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    colorDot: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      border: '2px solid black'
    },
    cardMeta: {
      fontWeight: 900,
      fontSize: '14px',
      marginTop: '6px',
      color: '#666'
    },
    contactActions: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      marginTop: '8px'
    },
    contactBtn: {
      border: '3px solid #000000',
      padding: '6px 12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '12px'
    },
    textBtn: {
      background: '#008000',
      color: '#FFFFFF'
    },
    emailBtn: {
      background: '#0000FF',
      color: '#FFFFFF'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '4px 10px',
      border: '2px solid black',
      fontWeight: 900,
      fontSize: '12px',
      marginTop: '8px',
      backgroundColor: '#f0f0f0'
    },
    cardActions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    editBtn: {
      background: '#754C38',
      color: '#FFD400',
      border: '3px solid #E8761D',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    toggleBtn: {
      background: '#ffffcc',
      color: 'black',
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    deleteBtn: {
      background: '#ff4444',
      color: 'white',
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    saveBtn: {
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '16px 24px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '18px',
      width: isMobile ? '100%' : 'auto'
    },
    modal: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 5000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: 'white',
      border: '8px solid black',
      padding: '24px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    modalTitle: {
      fontWeight: 900,
      fontSize: '22px',
      marginBottom: '16px',
      textTransform: 'uppercase'
    },
    modalBtnRow: { display: 'flex', gap: '10px', marginTop: '16px' },
    confirmBtn: {
      flex: 1,
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    cancelBtn: {
      flex: 1,
      background: 'white',
      color: 'black',
      border: '4px solid black',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    colorGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))',
      gap: '8px',
      marginTop: '10px'
    },
    colorOption: {
      width: '50px',
      height: '50px',
      border: '3px solid black',
      cursor: 'pointer',
      borderRadius: '4px'
    },
    colorOptionActive: {
      border: '4px solid #2D5F3F',
      transform: 'scale(1.1)'
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginTop: '8px'
    },
    loadingBox: {
      padding: '30px',
      textAlign: 'center',
      fontWeight: 900,
      fontSize: '16px'
    }
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jf-emp-hover:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
      `}</style>
      <div style={styles.contentWrapper}>
        <TitleBar
          icon="👷"
          title="EMPLOYEES"
          accent="#E8761D"
          right={
            <button
              className="jf-emp-hover"
              onClick={() => navigate('/settings')}
              style={{
                background: 'black',
                color: '#FFA500',
                border: '5px solid #E8761D',
                padding: '14px',
                fontWeight: 900,
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING EMPLOYEES...</div>
          ) : (
            <>
              <button className="jf-emp-hover" style={styles.addBtn} onClick={() => setShowAddEmployee(true)}>
                + ADD EMPLOYEE
              </button>

              {employees.length === 0 ? (
                <div style={{ padding: '20px', border: '4px solid black', backgroundColor: 'white', fontWeight: 900 }}>
                  No employees added yet.
                </div>
              ) : (
                sortedEmployees.map(employee => (
                  <div key={employee.id} style={styles.card}>
                    <div style={styles.cardHeader}>
                      <div style={styles.cardInfo}>
                        <div style={styles.cardName}>
                          <div style={{ ...styles.colorDot, backgroundColor: employee.color }} />
                          {employee.name}
                        </div>
                        <div style={styles.cardMeta}>{employee.email}</div>
                        {employee.phone && <div style={styles.cardMeta}>{employee.phone}</div>}
                        {(() => {
                          const storedPhone = String(employee.phone ?? '').trim();
                          const normalizedPhone = storedPhone.startsWith('+')
                            ? `+${storedPhone.slice(1).replace(/\D/g, '')}`
                            : storedPhone.replace(/\D/g, '');
                          const phoneDigitCount = normalizedPhone.replace(/\D/g, '').length;
                          const textHref = phoneDigitCount >= 7 && phoneDigitCount <= 15
                            ? `sms:${normalizedPhone}`
                            : '';
                          const storedEmail = String(employee.email ?? '').trim();
                          const emailHref = storedEmail ? `mailto:${storedEmail}` : '';

                          return textHref || emailHref ? (
                            <div style={styles.contactActions}>
                              {textHref ? (
                                <button
                                  type="button"
                                  className="jf-emp-hover"
                                  style={{ ...styles.contactBtn, ...styles.textBtn }}
                                  onClick={() => { window.location.href = textHref; }}
                                >
                                  TEXT
                                </button>
                              ) : null}
                              {emailHref ? (
                                <button
                                  type="button"
                                  className="jf-emp-hover"
                                  style={{ ...styles.contactBtn, ...styles.emailBtn }}
                                  onClick={() => { window.location.href = emailHref; }}
                                >
                                  EMAIL
                                </button>
                              ) : null}
                            </div>
                          ) : null;
                        })()}
                        <div style={styles.statusBadge}>
                          {employee.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </div>
                      </div>

                      <div style={styles.cardActions}>
                        <button className="jf-emp-hover" style={styles.toggleBtn} onClick={() => toggleActive(employee.id)}>
                          {employee.isActive ? 'SET INACTIVE' : 'SET ACTIVE'}
                        </button>
                        <button className="jf-emp-hover" style={styles.editBtn} onClick={() => startEditEmployee(employee)}>
                          EDIT
                        </button>
                        <button className="jf-emp-hover" style={styles.deleteBtn} onClick={() => deleteEmployee(employee.id)}>
                          DELETE
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}

              <div style={{ marginTop: '24px' }}>
                {unsavedWarningVisible && (
                  <NotificationOverlayFrame accentColor="#FFC107" role="status" ariaLive="polite">
                    <div style={{ fontWeight: 900, fontSize: 'clamp(18px, 4.8vw, 22px)', lineHeight: 1.2 }}>
                      EMPLOYEE CHANGES NOT SAVED YET
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 'clamp(15px, 4vw, 18px)', lineHeight: 1.35, marginTop: '6px' }}>
                      Select SAVE ALL CHANGES to keep your changes.
                    </div>
                  </NotificationOverlayFrame>
                )}
                <button className="jf-emp-hover" style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                  {saving ? 'SAVING...' : 'SAVE ALL CHANGES'}
                </button>
              </div>
            </>
          )}
        </div>

        {showAddEmployee && (
          <div style={styles.modal} onClick={() => setShowAddEmployee(false)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>ADD EMPLOYEE</h2>

              <div style={styles.row1}>
                <label style={styles.label}>EMPLOYEE NAME *</label>
                <input
                  style={styles.input}
                  value={employeeForm.name}
                  onChange={e => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>

              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>EMAIL *</label>
                  <input
                    style={styles.input}
                    type="email"
                    value={employeeForm.email}
                    onChange={e => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label style={styles.label}>PHONE</label>
                  <input
                    style={styles.input}
                    value={employeeForm.phone}
                    onChange={e => handlePhoneChange(e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>CALENDAR COLOR</label>
                <div style={styles.colorGrid}>
                  {PREDEFINED_COLORS.map(color => (
                    <div
                      key={color}
                      style={{
                        ...styles.colorOption,
                        backgroundColor: color,
                        ...(employeeForm.color === color ? styles.colorOptionActive : {})
                      }}
                      onClick={() => setEmployeeForm({ ...employeeForm, color })}
                    />
                  ))}
                </div>
              </div>

              <div style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={!!employeeForm.isActive}
                  onChange={e => setEmployeeForm({ ...employeeForm, isActive: e.target.checked })}
                  style={{ transform: 'scale(1.3)' }}
                />
                <div style={{ fontWeight: 900, textTransform: 'uppercase' }}>
                  ACTIVE
                </div>
              </div>

              <div style={styles.modalBtnRow}>
                <button className="jf-emp-hover" style={styles.confirmBtn} onClick={addEmployee}>ADD</button>
                <button
                  className="jf-emp-hover"
                  style={styles.cancelBtn}
                  onClick={() => {
                    setShowAddEmployee(false);
                    setEmployeeForm({ name: '', email: '', phone: '', color: '#3b82f6', isActive: true });
                  }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}

        {editingEmployee && (
          <div style={styles.modal} onClick={() => setEditingEmployee(null)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>EDIT EMPLOYEE</h2>

              <div style={styles.row1}>
                <label style={styles.label}>EMPLOYEE NAME *</label>
                <input
                  style={styles.input}
                  value={employeeForm.name}
                  onChange={e => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                />
              </div>

              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>EMAIL *</label>
                  <input
                    style={styles.input}
                    type="email"
                    value={employeeForm.email}
                    onChange={e => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>PHONE</label>
                  <input
                    style={styles.input}
                    value={employeeForm.phone}
                    onChange={e => handlePhoneChange(e.target.value)}
                  />
                </div>
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>CALENDAR COLOR</label>
                <div style={styles.colorGrid}>
                  {PREDEFINED_COLORS.map(color => (
                    <div
                      key={color}
                      style={{
                        ...styles.colorOption,
                        backgroundColor: color,
                        ...(employeeForm.color === color ? styles.colorOptionActive : {})
                      }}
                      onClick={() => setEmployeeForm({ ...employeeForm, color })}
                    />
                  ))}
                </div>
              </div>

              <div style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={!!employeeForm.isActive}
                  onChange={e => setEmployeeForm({ ...employeeForm, isActive: e.target.checked })}
                  style={{ transform: 'scale(1.3)' }}
                />
                <div style={{ fontWeight: 900, textTransform: 'uppercase' }}>
                  ACTIVE
                </div>
              </div>

              <div style={styles.modalBtnRow}>
                <button className="jf-emp-hover" style={styles.confirmBtn} onClick={saveEditEmployee}>SAVE</button>
                <button
                  className="jf-emp-hover"
                  style={styles.cancelBtn}
                  onClick={() => {
                    setEditingEmployee(null);
                    setEmployeeForm({ name: '', email: '', phone: '', color: '#3b82f6', isActive: true });
                  }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeesSettings;
